import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const STORAGE_KEY = "vsp_banner_deadline";
const DISMISS_KEY = "vsp_banner_dismissed";

function pad2(n: number) {
  return n.toString().padStart(2, "0");
}

function getRemaining(deadline: number) {
  const now = Date.now();
  const diff = Math.max(0, deadline - now);
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { diff, hours, minutes, seconds };
}

export default function PromoBanner() {
  const [deadline] = useState<number>(() => {
    const saved =
      typeof window !== "undefined"
        ? window.localStorage.getItem(STORAGE_KEY)
        : null;
    if (saved) return Number(saved);
    const d = Date.now() + 24 * 60 * 60 * 1000; // 24h
    if (typeof window !== "undefined")
      window.localStorage.setItem(STORAGE_KEY, String(d));
    return d;
  });

  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(DISMISS_KEY) === "1";
  });

  const [{ hours, minutes, seconds, diff }, setRemaining] = useState(() =>
    getRemaining(deadline)
  );
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    intervalRef.current = window.setInterval(() => {
      setRemaining(getRemaining(deadline));
    }, 1000);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [deadline]);

  if (diff <= 0 || dismissed) return null;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (typeof window !== "undefined")
      window.localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const handleClick = () => {
    if (typeof window !== "undefined") window.location.assign("/pricing");
  };

  const Group = ({ value, label }: { value: string; label: string }) => (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
      <Typography
        component="span"
        sx={{
          fontWeight: 900,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
          fontSize: { xs: "1.25rem", sm: "1.6rem", md: "1.9rem" },
          letterSpacing: { xs: 1, md: 2 },
        }}
      >
        {value}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontWeight: 400,
          opacity: 0.9,
          letterSpacing: 2,
          fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" },
          textTransform: "uppercase",
          paddingBottom: "2px",
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1300,
        width: "100%",
        bgcolor: "#000",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 1.5,
        px: 2,
        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        cursor: "pointer",
      }}
      onClick={handleClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 2, md: 2 },
          flexWrap: "wrap",
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        {/* <AccessTimeRoundedIcon fontSize="small" /> */}
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "0.9rem", md: "1rem" },
            paddingRight: 3,
          }}
        >
          Limited Time Offer: 50% OFF Yearly Plans!
        </Typography>

        <Typography component="span" sx={{ fontWeight: 500, opacity: 0.95 }}>
          Ends in
        </Typography>

        {/* Timer: 00 DAYS 09 HOURS 05 MINS 03 SECS */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 2 } }}
        >
          <Group
            value={`${pad2(Math.floor(diff / (1000 * 60 * 60 * 24)))}`}
            label="Days"
          />
          <Group value={`${pad2(hours % 24)}`} label="Hours" />
          <Group value={`${pad2(minutes)}`} label="Mins" />
          <Group value={`${pad2(seconds)}`} label="Secs" />
        </Box>
      </Box>

      <IconButton
        aria-label="Dismiss promotion"
        onClick={handleDismiss}
        sx={{
          color: "#fff",
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <CloseRoundedIcon />
      </IconButton>
    </Box>
  );
}
